import React from 'react';
import '../../styles/components/AsperaSendPackage.scss';
import { AsperaAutocomplete } from '../AsperaAutocomplete';
import { openFileFolderPicker, monitorDrops, getTransferSystemStatus, watchConnectStatusChange, runningWithAsperaMobile } from '../../helpers/transfer';
import { Link, FormGroup, Button, getIconComponent } from '../Carbon';
import { language } from '../../services/LanguageService';
import { notification } from '../../services/NotificationService';
import { AsperaForm } from '../AsperaForm';
import { SubManager } from '../../classes/SubManager';
import { AsperaError } from '../../classes/AsperaError';
import { iterableToArray, getFilenameFromPath } from '../../helpers/helpers';
import { tracking } from '../../services/TrackingService';
import { AsperaOverflowButton } from '../AsperaOverflowButton';
import { AsperaTooltip } from '../AsperaTooltip';
import { AsperaAccordion, AsperaAccordionItem } from '../AsperaAccordion';
import { timeUnits } from '../AsperaNumberUnitInput';
import { AsperaMarkdownRender } from '../AsperaMarkdownRender';
/**
 * React Component
 *
 * The AsperaSendPackage renders the send package form
 *
 * @example
 * ```javascript
 * <AsperaSendPackage toProps={toProps} bccProps={bccProps} />
 * ```
 */
export class AsperaSendPackage extends React.Component {
    constructor() {
        super(...arguments);
        this.state = {
            /** Indicate if BCC field should be shown */
            showBcc: false,
            /** Indicate if notifications field should be shown */
            showNotifications: false,
        };
        this.subManager = new SubManager();
        this.files = new Map();
        this.remoteFiles = new Map();
        this.disableDownloadNever = false;
        this.isExpirationPolicyValid = true;
        this.dropDirty = false;
        this.filePickerSub = '';
        this.isTitleValid = true;
        this.isMessageValid = true;
        this.isReleasePolicyValid = true;
        this.passwordData = {
            main: '',
            confirm: '',
        };
        this.releaseData = {
            hasReleasePolicy: false,
            setLater: false,
            date: undefined,
        };
        this.expirationData = {
            hasExpirationByTime: false,
            policy: 'none',
            timeInSeconds: undefined,
        };
        this.openBcc = () => {
            this.setState({ showBcc: true });
        };
        this.validCheck = () => {
            const { isInternalFormValidCallback, requirePasswordProtection, pointers } = this.props;
            if (typeof isInternalFormValidCallback === 'function') {
                let isPasswordValid = this.passwordData.main ? this.passwordData.main === this.passwordData.confirm : true;
                if (requirePasswordProtection) {
                    isPasswordValid = !!this.passwordData.main && (this.passwordData.main === this.passwordData.confirm);
                }
                const hasExpirationPolicy = !!(pointers.expirationPolicy || pointers.expirationTime);
                isInternalFormValidCallback(this.isTitleValid && this.isMessageValid && isPasswordValid && this.isReleasePolicyValid && (!hasExpirationPolicy || this.isExpirationPolicyValid));
            }
        };
        this.illegalCharsCheck = (field, value, illegalChars) => {
            const foundIllegal = illegalChars === null || illegalChars === void 0 ? void 0 : illegalChars.some(char => value.indexOf(char) > -1);
            if (field === 'title') {
                this.isTitleValid = !foundIllegal;
            }
            else {
                this.isMessageValid = !foundIllegal;
            }
            return !foundIllegal;
        };
        this.removeRemoteFile = (file) => {
            const { remoteFileOnChange } = this.props;
            this.remoteFiles.delete(file.name);
            remoteFileOnChange(iterableToArray(this.remoteFiles.values()));
            this.setState({});
            tracking.track('Started Process', {
                process: 'Send Package Removed Single Remote File',
                successFlag: true,
                processType: 'package',
            });
        };
        this.removeFile = (file) => {
            const { fileOnChange } = this.props;
            this.files.delete(file.name);
            fileOnChange(iterableToArray(this.files.values()));
            this.setState({});
            tracking.track('Started Process', {
                process: 'Send Package Removed Single Local File',
                successFlag: true,
                processType: 'package',
            });
        };
        this.sendNotificationForDuplicateNames = (fileNames) => {
            if (fileNames === null || fileNames === void 0 ? void 0 : fileNames.length) {
                notification.create('warning', language.get('sendPackage.unableAddFiles'), language.get('sendPackage.unableAddFilesHelper', [fileNames.join(', ')]));
            }
        };
        this.validateFiles = (selectedFiles) => {
            const connectFileNames = [...this.files.values()].map(item => (getFilenameFromPath(item.name) || '').toLowerCase());
            const remoteFileNames = [...this.remoteFiles.values()].map(item => (getFilenameFromPath(item.name) || '').toLowerCase());
            const allFileNames = new Set([...connectFileNames, ...remoteFileNames]);
            return selectedFiles.map(file => ({
                file,
                isDuplicate: allFileNames.has(getFilenameFromPath(file.name).toLowerCase()),
            }));
        };
        this.addFileFolder = (folder) => {
            const { transferId } = this.props;
            this.filePickerSub = this.subManager.setSubscription(openFileFolderPicker(transferId, folder).subscribe({
                next: data => {
                    this.addFiles(data.dataTransfer.files);
                    this.subManager.unsubscribe(this.filePickerSub);
                },
                error: error => {
                    new AsperaError('AsperaSendPackage: Unable to get files or folders', error);
                    notification.create('error', language.get('sendPackage.unableAddFilesFolders'));
                    this.subManager.unsubscribe(this.filePickerSub);
                },
            }));
        };
        this.openNotifications = () => {
            this.setState({ showNotifications: true });
        };
        this.clearFiles = () => {
            const { fileOnChange, remoteFileOnChange } = this.props;
            this.files.clear();
            this.remoteFiles.clear();
            fileOnChange(iterableToArray(this.files.values()));
            remoteFileOnChange(iterableToArray(this.remoteFiles.values()));
            this.setState({});
            tracking.track('Started Process', {
                process: 'Send Package File List Cleared',
                successFlag: true,
                processType: 'package',
            });
        };
    }
    addFiles(files) {
        const { fileOnChange, readOnlyFiles } = this.props;
        if (readOnlyFiles)
            return;
        const selectedFiles = this.validateFiles(files);
        const validFiles = selectedFiles.filter(files => !files.isDuplicate).map(f => f.file);
        const invalidFileNames = selectedFiles.filter(files => files.isDuplicate).map(f => f.file.name);
        validFiles.forEach(file => this.files.set(file.name, file));
        fileOnChange([...this.files.values()]);
        this.sendNotificationForDuplicateNames(invalidFileNames);
        this.setState({});
    }
    get publicLinkSettings() {
        const { pointers, getPublicLinksProps, light, model, disabled, ignoreDirtyRules } = this.props;
        if (typeof getPublicLinksProps === 'function' && pointers.publicLink) {
            const publicLinkData = getPublicLinksProps();
            const formProps = {
                light,
                ignoreDirtyRules,
                formDetails: {
                    id: 'AsperaSendPackage-PublicLink',
                    model,
                    items: [
                        {
                            type: 'toggle',
                            pointer: pointers.publicLink,
                            label: publicLinkData.label,
                            disabled: publicLinkData.disabled || disabled,
                            tooltipContent: publicLinkData.tooltipContent,
                            helperText: publicLinkData.helperText,
                        },
                    ],
                },
            };
            return (React.createElement("div", { className: "public-link-area-wrapper" },
                React.createElement(AsperaForm, Object.assign({}, formProps))));
        }
        else {
            return null;
        }
    }
    get instructionsArea() {
        const { instructions } = this.props;
        if (instructions) {
            return React.createElement("div", { className: "package-instructions" },
                React.createElement("div", { className: "title" },
                    " ",
                    language.get('sendPackage.instructions'),
                    " "),
                React.createElement(AsperaMarkdownRender, { markdown: instructions }));
        }
        else {
            return null;
        }
    }
    get recipientsArea() {
        const { toProps, bccProps, light, getPublicLinksProps, sendNotificationProps, pointers, disabled, toFieldExtra, bccFieldExtra } = this.props;
        const { showBcc, showNotifications } = this.state;
        return (bccProps || toProps || (sendNotificationProps === null || sendNotificationProps === void 0 ? void 0 : sendNotificationProps.length)) ? (React.createElement("div", { className: `recipients-area ${(typeof getPublicLinksProps === 'function' && pointers.publicLink) ? 'has-public-link-area' : ''}` },
            this.instructionsArea,
            React.createElement(FormGroup, { legendText: "", className: "recipients-area-to-wrapper" },
                React.createElement("div", { className: "link-actions-wrapper" },
                    !showNotifications && !!(sendNotificationProps === null || sendNotificationProps === void 0 ? void 0 : sendNotificationProps.length) && React.createElement(Link, { className: "notifications-link", onClick: this.openNotifications, "data-auto": "send-package-link-notifications" }, language.get('sendPackage.sendNotifications')),
                    !!bccProps && !showBcc && React.createElement(Link, { className: "bcc-link", onClick: this.openBcc, "data-auto": "send-package-link-bcc" }, language.get('sendPackage.bcc'))),
                !!toProps && React.createElement(AsperaAutocomplete, Object.assign({ disabled: disabled }, toProps, { light: light, "data-auto": "send-package-to" })),
                !!(toFieldExtra && toProps) && React.createElement("div", { className: "custom-inject-recipient-area" }, toFieldExtra)),
            !!bccProps && showBcc && (React.createElement(FormGroup, { legendText: "" },
                React.createElement(AsperaAutocomplete, Object.assign({ disabled: disabled }, bccProps, { light: light, "data-auto": "send-package-bcc" })),
                !!bccFieldExtra && React.createElement("div", { className: "custom-inject-recipient-area" }, bccFieldExtra))),
            showNotifications && !!(sendNotificationProps === null || sendNotificationProps === void 0 ? void 0 : sendNotificationProps.length) && (sendNotificationProps.map((autocompleteProp, index) => {
                return (React.createElement(FormGroup, { key: index, legendText: "", className: "send-notification-item" },
                    React.createElement(AsperaAutocomplete, Object.assign({ disabled: disabled }, autocompleteProp, { light: light, "data-auto": `send-package-send-notification-${index}` }))));
            })),
            this.publicLinkSettings)) : (this.instructionsArea ? (React.createElement("div", { className: "recipients-area" }, this.instructionsArea)) : null);
    }
    get packageTitleField() {
        var _a, _b;
        const { model, pointers, onChange, light, titleRestrictions, disabled, ignoreDirtyRules } = this.props;
        const helperText = pointers.titleHelperText ? [pointers.titleHelperText] : [];
        if ((_a = titleRestrictions === null || titleRestrictions === void 0 ? void 0 : titleRestrictions.illegalChars) === null || _a === void 0 ? void 0 : _a.length) {
            helperText.push(language.get('sendPackage.helperTextIllegalChar', [titleRestrictions.illegalChars.join(', ')]));
        }
        if (titleRestrictions === null || titleRestrictions === void 0 ? void 0 : titleRestrictions.maxLength) {
            helperText.push(language.get('sendPackage.helperTextMaxLength', [titleRestrictions.maxLength]));
        }
        const helper = helperText.join(', ');
        const formDetails = {
            id: 'AsperaSendPackage-PackageTitle',
            model,
            items: [
                {
                    type: 'text',
                    pointer: pointers.title,
                    disabled: disabled,
                    label: language.get('sendPackage.packageTitle'),
                    required: true,
                    helperText: helper,
                    onChange: (value, changeModel) => {
                        this.illegalCharsCheck('title', value, titleRestrictions === null || titleRestrictions === void 0 ? void 0 : titleRestrictions.illegalChars);
                        this.validCheck();
                        if (typeof onChange === 'function')
                            onChange(value, changeModel);
                    },
                    maxLength: (titleRestrictions === null || titleRestrictions === void 0 ? void 0 : titleRestrictions.maxLength) ? Number(titleRestrictions.maxLength) : undefined,
                    validationCheck: (value) => this.illegalCharsCheck('title', value, titleRestrictions === null || titleRestrictions === void 0 ? void 0 : titleRestrictions.illegalChars),
                    errorText: language.get('sendPackage.errorTextIllegalChar', [language.get('sendPackage.helperTextIllegalChar', [(_b = titleRestrictions === null || titleRestrictions === void 0 ? void 0 : titleRestrictions.illegalChars) === null || _b === void 0 ? void 0 : _b.join(', ')])]),
                },
            ],
        };
        return React.createElement(AsperaForm, { formDetails: formDetails, light: light, ignoreDirtyRules: ignoreDirtyRules });
    }
    get packageMessageField() {
        var _a, _b;
        const { model, pointers, onChange, light, messageRestrictions, disabled, ignoreDirtyRules } = this.props;
        const helperText = pointers.messageHelperText ? [pointers.messageHelperText] : [];
        if ((_a = messageRestrictions === null || messageRestrictions === void 0 ? void 0 : messageRestrictions.illegalChars) === null || _a === void 0 ? void 0 : _a.length) {
            helperText.push(language.get('sendPackage.helperTextIllegalChar', [messageRestrictions.illegalChars.join(', ')]));
        }
        if (messageRestrictions === null || messageRestrictions === void 0 ? void 0 : messageRestrictions.maxLength) {
            helperText.push(language.get('sendPackage.helperTextMaxLength', [messageRestrictions.maxLength]));
        }
        const helper = helperText.join(', ');
        const formDetails = {
            id: 'AsperaSendPackage-PackageMessage',
            model,
            items: [
                {
                    type: 'textarea',
                    disabled: disabled,
                    hidden: () => !pointers.message,
                    pointer: pointers.message,
                    label: language.get('sendPackage.packageMessage'),
                    helperText: helper,
                    onChange: (value, changeModel) => {
                        this.illegalCharsCheck('message', value, messageRestrictions === null || messageRestrictions === void 0 ? void 0 : messageRestrictions.illegalChars);
                        this.validCheck();
                        if (typeof onChange === 'function')
                            onChange(value, changeModel);
                    },
                    maxLength: (messageRestrictions === null || messageRestrictions === void 0 ? void 0 : messageRestrictions.maxLength) ? Number(messageRestrictions.maxLength) : undefined,
                    validationCheck: (value) => this.illegalCharsCheck('message', value, messageRestrictions === null || messageRestrictions === void 0 ? void 0 : messageRestrictions.illegalChars),
                    errorText: language.get('sendPackage.errorTextIllegalChar', [language.get('sendPackage.helperTextIllegalChar', [(_b = messageRestrictions === null || messageRestrictions === void 0 ? void 0 : messageRestrictions.illegalChars) === null || _b === void 0 ? void 0 : _b.join(', ')])]),
                },
            ],
        };
        return React.createElement(AsperaForm, { formDetails: formDetails, light: light, ignoreDirtyRules: ignoreDirtyRules });
    }
    get fileList() {
        const { light, readOnlyFiles } = this.props;
        const allFiles = iterableToArray(this.files.values()).concat(iterableToArray(this.remoteFiles.values()));
        const maxFiles = 200;
        const totalFiles = this.files.size + this.remoteFiles.size;
        const scrollFileList = totalFiles > 12;
        return (React.createElement("div", { className: "file-container" },
            totalFiles >= 9 && React.createElement("div", { className: "file-container--clear" },
                React.createElement(Link, { onClick: this.clearFiles }, language.get('sendPackage.removeAll'))),
            React.createElement("div", { className: `file-container--list ${!allFiles.length ? 'has-no-files' : ''} ${scrollFileList ? 'wrap-file-list' : ''}` },
                allFiles.slice(0, maxFiles).map((file, index, list) => {
                    const totalRenderedFiles = list.length;
                    const reverseTooltip = scrollFileList ? (totalRenderedFiles - 4) < index : false;
                    const nameToUse = getFilenameFromPath(file.name);
                    const showPath = nameToUse !== file.name;
                    const hasExtraData = file.size || showPath || file.lastModifiedDate;
                    const tooltipLabel = (React.createElement(React.Fragment, null,
                        showPath && React.createElement("p", { className: "send-package-file-item--tooltip-data" }, language.get('sendPackage.pathInfo', [file.name])),
                        !!file.size && React.createElement("p", { className: "send-package-file-item--tooltip-data" }, language.get('sendPackage.sizeInfo', [language.getBytes(file.size, 1)])),
                        !!file.lastModifiedDate && React.createElement("p", { className: "send-package-file-item--tooltip-data" }, language.get('sendPackage.lastModifiedInfo', [language.getDate(file.lastModifiedDate, 'short')]))));
                    return (React.createElement("div", { className: `send-package-file-item ${light ? 'light-mode' : ''}`, key: index },
                        React.createElement("span", { className: "send-package-file-item--name", title: file.name }, nameToUse),
                        hasExtraData && React.createElement(AsperaTooltip, { useTextOnly: true, align: reverseTooltip ? 'top-right' : 'bottom-right' }, tooltipLabel),
                        !readOnlyFiles && React.createElement(Button, { className: "send-package-file-item--action", "data-auto": `send-package-file-item${nameToUse}-remove`, hasIconOnly: true, size: "sm", kind: "ghost", iconDescription: language.get('sendPackage.removeFile'), tooltipAlignment: "end", tooltipPosition: language.leftRightStyle ? 'right' : 'left', renderIcon: getIconComponent('Close'), onClick: () => {
                                this.dropDirty = true;
                                if (file.data) {
                                    this.removeRemoteFile(file);
                                }
                                else {
                                    this.removeFile(file);
                                }
                            } })));
                }),
                allFiles.length > maxFiles && React.createElement("div", { className: "too-many-files" }, language.get('sendPackage.tooManyFiles', [allFiles.length - maxFiles])))));
    }
    get fileUploadButton() {
        const { importFromRemoteCallback, importFromRemoteText } = this.props;
        const items = [
            {
                name: language.get('sendPackage.files'),
                onClick: () => {
                    this.dropDirty = true;
                    this.addFileFolder();
                },
                id: 'files-button',
                hidden: getTransferSystemStatus().type === 'none',
            },
            {
                name: language.get('sendPackage.folders'),
                onClick: () => {
                    this.dropDirty = true;
                    this.addFileFolder(true);
                },
                id: 'folders-button',
                hidden: getTransferSystemStatus().type === 'none',
            },
            {
                hidden: !(importFromRemoteCallback && importFromRemoteText),
                name: importFromRemoteText,
                onClick: () => {
                    this.dropDirty = true;
                    if (importFromRemoteCallback)
                        importFromRemoteCallback();
                },
                id: 'remote-button',
            },
        ];
        if (!items.filter(item => !item.hidden).length) {
            return null;
        }
        return (React.createElement(AsperaOverflowButton, { name: language.get('sendPackage.addFiles'), icon: getIconComponent('Add'), kind: "tertiary", id: "add-file-button", items: items }));
    }
    get invalidDropzone() {
        const hasFiles = !!(this.files.size + this.remoteFiles.size);
        return (this.dropDirty && !hasFiles);
    }
    get dropZone() {
        if (getTransferSystemStatus().type === 'none') {
            return null;
        }
        return (React.createElement("div", { className: `drop-zone ${this.invalidDropzone ? 'drop-zone-error' : ''}`, "data-auto": "send-package-dropzone" },
            React.createElement("span", { className: "label-text" }, language.get('sendPackage.dropFilesText'))));
    }
    get fileUploadArea() {
        const { readOnlyFiles, disabled } = this.props;
        return (React.createElement("div", { className: "file-upload-area" },
            (!readOnlyFiles && !disabled) &&
                React.createElement(React.Fragment, null,
                    this.fileUploadButton,
                    this.dropZone,
                    this.invalidDropzone && React.createElement("div", { className: "cds--form-requirement drop-zone-error-text" }, language.get('forms.requireItem'))),
            this.fileList));
    }
    // TODO: Add time field once AsperaTimePicker is ready (Time from Carbon is pretty messy)
    get releasePolicy() {
        const { model, pointers, onChange, light, disabled, ignoreDirtyRules, releasePolicyHelper } = this.props;
        if (pointers.releaseDate && model[pointers.releaseDate])
            this.releaseData.date = model[pointers.releaseDate];
        if (pointers.releaseDate && model[pointers.releaseDate] && !pointers.releasePolicy) {
            this.releaseData.hasReleasePolicy = true;
        }
        else if (pointers.releasePolicy && model[pointers.releasePolicy]) {
            if (model[pointers.releasePolicy] === 'now')
                this.releaseData.hasReleasePolicy = false;
            if (model[pointers.releasePolicy] === 'on_date')
                this.releaseData.hasReleasePolicy = true;
            if (model[pointers.releasePolicy] === 'later') {
                this.releaseData.hasReleasePolicy = true;
                this.releaseData.setLater = true;
            }
        }
        const setReleaseDateLaterChange = (value) => {
            if (pointers.releaseDate) {
                model[pointers.releaseDate] = undefined;
                this.releaseData.date = undefined;
            }
            if (pointers.releasePolicy) {
                model[pointers.releasePolicy] = value ? 'later' : 'on_date';
            }
            this.isReleasePolicyValid = value ? true : !!this.releaseData.date;
            this.validCheck();
            if (typeof onChange === 'function')
                onChange(value, model);
            this.setState({});
        };
        const setReleasePolicyChange = (value) => {
            if (pointers.releaseDate) {
                model[pointers.releaseDate] = undefined;
                this.releaseData.date = undefined;
                this.releaseData.setLater = false;
            }
            if (pointers.releasePolicy) {
                model[pointers.releasePolicy] = value ? 'on_date' : 'now';
            }
            this.isReleasePolicyValid = this.releaseData.hasReleasePolicy ? !!this.releaseData.date : true;
            this.validCheck();
            if (typeof onChange === 'function')
                onChange(value, model);
            this.setState({});
        };
        const setReleaseDateChange = (value) => {
            if (!pointers.releaseDate)
                return;
            model[pointers.releaseDate] = this.releaseData.date = value;
            this.isReleasePolicyValid = !!this.releaseData.date;
            this.validCheck();
            if (typeof onChange === 'function')
                onChange(value, model);
            this.setState({});
        };
        const setReleaseTimeChange = (data) => {
            if (!pointers.releaseDate)
                return;
            if (this.releaseData.date) {
                model[pointers.releaseDate] = this.releaseData.date = new Date(new Date(this.releaseData.date).setHours(data.hour, data.minute, 0, 0)).toISOString();
                this.validCheck();
            }
        };
        const formDetails = {
            id: 'AsperaSendPackage-ReleasePolicy',
            model: this.releaseData,
            items: [
                { type: 'toggle', disabled: disabled, pointer: 'hasReleasePolicy', onChange: setReleasePolicyChange, helperText: releasePolicyHelper },
            ],
        };
        const now = new Date();
        if (this.releaseData.hasReleasePolicy) {
            if (pointers.releasePolicy) {
                formDetails.items.push({ type: 'checkbox', disabled: disabled, pointer: 'setLater', label: language.get('sendPackage.setReleaseDateLater'), onChange: setReleaseDateLaterChange });
            }
            else {
                this.releaseData.setLater = false;
            }
            if (!this.releaseData.setLater)
                formDetails.items.push({ type: 'date', pointer: 'date', required: true, label: language.get('sendPackage.releaseDate'), onChange: setReleaseDateChange, dateProps: { minDate: new Date(+now.setHours(0, 0, 0, 0)).toISOString() } }, {
                    type: 'time',
                    pointer: 'time',
                    required: true,
                    disabled: () => !this.releaseData.date,
                    label: language.get('sendPackage.chooseTime'),
                    timeProps: {
                        onChange: setReleaseTimeChange,
                        initialValue: this.releaseData.date ? { hour: new Date(this.releaseData.date).getHours(), minute: new Date(this.releaseData.date).getMinutes() } : undefined,
                    },
                });
        }
        return (React.createElement(AsperaAccordionItem, { title: language.get('sendPackage.scheduleForLater'), dataAuto: "send-package-group-release-policy" },
            React.createElement(AsperaForm, { formDetails: formDetails, light: light, ignoreDirtyRules: ignoreDirtyRules })));
    }
    get passwordProtection() {
        const { model, pointers, onChange, light, requirePasswordProtection, disabled, ignoreDirtyRules } = this.props;
        const passwordChange = (value, formModel) => {
            if (pointers.password && typeof onChange === 'function') {
                if (formModel.main === formModel.confirm) {
                    this.validCheck();
                    model[pointers.password] = value;
                    onChange(value, model);
                }
                else {
                    model[pointers.password] = undefined;
                    this.validCheck();
                    onChange('', model);
                }
            }
        };
        const formDetails = {
            id: 'AsperaSendPackage-PasswordProtection',
            model: this.passwordData,
            items: [
                {
                    type: 'header',
                    helperText: language.get('sendPackage.passwordProtectionHelper'),
                },
                {
                    type: 'password',
                    disabled: disabled,
                    pointer: 'main',
                    maxLength: 63,
                    label: language.get('sendPackage.password'),
                    onChange: (value, model) => {
                        if (!value) {
                            this.passwordData.confirm = '';
                        }
                        passwordChange(value, model);
                    },
                    required: () => !!requirePasswordProtection,
                },
                {
                    type: 'password',
                    pointer: 'confirm',
                    maxLength: 63,
                    label: language.get('sendPackage.confirmPassword'),
                    instantTouch: true,
                    onChange: passwordChange,
                    validationCheck: (value) => {
                        return value === this.passwordData.main;
                    },
                    errorText: language.get('sendPackage.passwordMustMatch'),
                    required: true,
                    hidden: () => !(!!this.passwordData.main || disabled),
                },
            ],
        };
        const form = React.createElement(AsperaForm, { formDetails: formDetails, light: light, ignoreDirtyRules: ignoreDirtyRules });
        if (requirePasswordProtection) {
            return form;
        }
        else {
            return (React.createElement(AsperaAccordionItem, { title: language.get('sendPackage.passwordProtection'), dataAuto: "send-package-group-password-protection" }, form));
        }
    }
    get expirationPolicy() {
        const { light, model, pointers, onChange, disabled, ignoreDirtyRules } = this.props;
        if (pointers.expirationTime && !!this.maxExpirationSeconds) {
            this.expirationData.hasExpirationByTime = true;
        }
        if (pointers.expirationTime) {
            this.expirationData.timeInSeconds = model[pointers.expirationTime];
        }
        if (pointers.expirationPolicy && model[pointers.expirationPolicy]) {
            this.expirationData.policy = model[pointers.expirationPolicy];
        }
        const internalExpirationCheck = () => {
            const expirationTimeValid = this.maxExpirationSeconds ? !!this.expirationData.timeInSeconds && this.expirationData.timeInSeconds <= this.maxExpirationSeconds : true;
            const expirationPolicyValid = this.disableDownloadNever ? !!(this.expirationData.policy && this.expirationData.policy !== 'none') : true;
            this.isExpirationPolicyValid = expirationTimeValid && expirationPolicyValid;
        };
        const formDetails = {
            id: 'AsperaSendPackage-ExpirationPolicy',
            model: this.expirationData,
            items: [{
                    type: 'checkbox',
                    disabled: disabled || !!this.maxExpirationSeconds,
                    pointer: 'hasExpirationByTime',
                    hidden: () => !pointers.expirationTime,
                    collapseGroup: () => !!(this.expirationData.hasExpirationByTime && pointers.expirationTime),
                    label: language.get('sendPackage.deleteByTime'),
                    onChange: (value) => {
                        if (pointers.expirationTime && !value) {
                            model[pointers.expirationTime] = undefined;
                            this.expirationData.timeInSeconds = undefined;
                        }
                        if (typeof onChange === 'function')
                            onChange('', model);
                    },
                },
                {
                    type: 'number-unit',
                    pointer: 'timeInSeconds',
                    required: !!this.maxExpirationSeconds,
                    hidden: () => !(this.expirationData.hasExpirationByTime && pointers.expirationTime),
                    disabled,
                    numberProps: {
                        min: 0,
                        max: this.maxExpirationSeconds,
                        units: timeUnits(3),
                    },
                    onChange: (value) => {
                        model[pointers.expirationTime || ''] = Number(value);
                        internalExpirationCheck();
                        this.validCheck();
                        if (typeof onChange === 'function')
                            onChange('', model);
                    },
                },
                {
                    type: 'select',
                    label: language.get('sendPackage.deleteByDownload'),
                    pointer: 'policy',
                    required: this.disableDownloadNever,
                    hidden: () => !pointers.expirationPolicy,
                    disabled,
                    onChange: (value) => {
                        model[pointers.expirationPolicy || ''] = value;
                        internalExpirationCheck();
                        this.validCheck();
                        if (typeof onChange === 'function')
                            onChange('', model);
                    },
                    items: [
                        { value: 'none', text: language.get('sendPackage.never'), disabled: this.disableDownloadNever },
                        { value: 'delete_after_any_download', text: language.get('sendPackage.deleteAfterAnyDownloadsAll') },
                        { value: 'delete_after_all_download', text: language.get('sendPackage.deleteAfterAllDownloadsAll') },
                    ],
                }],
        };
        return (React.createElement(AsperaAccordionItem, { title: language.get('sendPackage.expirationPolicy'), dataAuto: "send-package-group-expiration-policy" },
            React.createElement(AsperaForm, { formDetails: formDetails, light: light, ignoreDirtyRules: ignoreDirtyRules })));
    }
    get transferOptions() {
        const { model, onChange, pointers, light, requireObfuscation, requireWatermarking, disabled, ignoreDirtyRules } = this.props;
        const formDetails = {
            id: 'AsperaSendPackage-TransferOptions',
            model: model,
            items: [],
        };
        /** Obfuscation is not supported by the Mobile SDK */
        if (pointers.obfuscation && !runningWithAsperaMobile())
            formDetails.items.push({ type: 'toggle', pointer: pointers.obfuscation, label: language.get('sendPackage.obfuscationFileName'), onChange: onChange, disabled: (requireObfuscation || disabled) });
        if (pointers.preventHttpDownload)
            formDetails.items.push({ type: 'toggle', disabled: disabled, pointer: pointers.preventHttpDownload, label: language.get('sendPackage.preventHttpDownload'), tooltipContent: language.get('sendPackage.preventHttpDownloadTooltip'), tooltipProps: { align: 'bottom-left', useTextOnly: true, children: undefined }, onChange: onChange });
        if (pointers.watermark)
            formDetails.items.push({ type: 'toggle', pointer: pointers.watermark, label: language.get('sendPackage.applyWatermarking'), tooltipContent: language.get('sendPackage.applyWatermarkingHelper'), onChange: onChange, disabled: (disabled || requireWatermarking) });
        return (React.createElement(AsperaAccordionItem, { title: language.get('sendPackage.moreTransferOptions'), dataAuto: "send-package-group-transfer-options" },
            React.createElement(AsperaForm, { formDetails: formDetails, light: light, ignoreDirtyRules: ignoreDirtyRules })));
    }
    get extraContentArea() {
        const { pointers, requirePasswordProtection } = this.props;
        return (React.createElement(AsperaAccordion, { className: "extra-content-area", dataAuto: "send-package-extra-options" },
            (!!pointers.releasePolicy || !!pointers.releaseDate) && this.releasePolicy,
            (!!pointers.password && !requirePasswordProtection) && this.passwordProtection,
            (!!pointers.expirationPolicy || !!pointers.expirationTime) && this.expirationPolicy,
            (!!pointers.obfuscation || !!pointers.preventHttpDownload || !!pointers.watermark) && this.transferOptions));
    }
    componentDidMount() {
        const { initialRemoteFiles, initialConnectFiles, remoteFileObservable, fileObservable, openBcc, expirationMaxObservable, disableDownloadNeverObservable, model, pointers, openNotification } = this.props;
        if (pointers.password && model[pointers.password]) {
            this.passwordData.main = model[pointers.password];
            this.passwordData.confirm = model[pointers.password];
        }
        if (Array.isArray(initialRemoteFiles)) {
            initialRemoteFiles.forEach(file => {
                this.remoteFiles.set(file.name, file);
            });
        }
        if (Array.isArray(initialConnectFiles)) {
            initialConnectFiles.forEach(file => {
                this.files.set(file.name, file);
            });
        }
        if (Array.isArray(initialConnectFiles) || Array.isArray(initialRemoteFiles)) {
            this.setState({});
        }
        this.subManager.setSubscription(watchConnectStatusChange().subscribe({
            next: () => {
                this.setState({});
            },
            error: error => {
                new AsperaError('AsperaSendPackage: connect status update encountered error', error);
            },
        }));
        if (remoteFileObservable) {
            const { remoteFileOnChange } = this.props;
            this.subManager.setSubscription(remoteFileObservable.subscribe({
                next: files => {
                    if (files) {
                        const selectedFiles = this.validateFiles(files);
                        const validFiles = selectedFiles.filter(files => !files.isDuplicate).map(f => f.file);
                        const inValidFileNames = selectedFiles.filter(files => files.isDuplicate).map(f => f.file.name);
                        validFiles.forEach(file => this.remoteFiles.set(file.name, file));
                        remoteFileOnChange(iterableToArray(this.remoteFiles.values()));
                        this.sendNotificationForDuplicateNames(inValidFileNames);
                    }
                    else {
                        this.remoteFiles.clear();
                    }
                    this.setState({});
                },
                error: error => {
                    new AsperaError('AsperaSendPackage: Unable to subscribe to remote file watcher', error);
                },
            }));
        }
        if (fileObservable) {
            this.subManager.setSubscription(fileObservable.subscribe({
                next: files => {
                    this.files.clear();
                    files.forEach(file => {
                        this.files.set(file.name, file);
                    });
                    this.setState({});
                },
                error: error => {
                    new AsperaError('AsperaSendPackage: Unable to subscribe to local file watcher', error);
                },
            }));
        }
        if (expirationMaxObservable) {
            this.subManager.setSubscription(expirationMaxObservable.subscribe({
                next: value => {
                    this.maxExpirationSeconds = value;
                    this.setState({});
                },
                error: error => {
                    new AsperaError('AsperaSendPackage: Unable to subscribe to expiration max watcher', error);
                },
            }));
        }
        if (disableDownloadNeverObservable) {
            this.subManager.setSubscription(disableDownloadNeverObservable.subscribe({
                next: value => {
                    this.disableDownloadNever = value;
                    this.setState({});
                },
                error: error => {
                    new AsperaError('AsperaSendPackage: Unable to subscribe to disable never download all or any watcher', error);
                },
            }));
        }
        this.subManager.setSubscription(monitorDrops().subscribe({
            next: data => {
                if (data.files.length) {
                    this.addFiles(data.files);
                }
            },
            error: error => {
                new AsperaError('AsperaSendPackage: Unable to subscribe to external file drop Event', error);
            },
        }));
        if (openBcc || openNotification) {
            this.setState({ showBcc: !!openBcc, showNotifications: !!openNotification });
        }
    }
    componentDidUpdate(previousProps) {
        const { pointers } = this.props;
        if (pointers.title !== previousProps.pointers.title ||
            pointers.titleHelperText !== previousProps.pointers.titleHelperText ||
            pointers.message !== previousProps.pointers.message ||
            pointers.messageHelperText !== previousProps.pointers.messageHelperText ||
            pointers.password !== previousProps.pointers.password ||
            pointers.obfuscation !== previousProps.pointers.obfuscation ||
            pointers.preventHttpDownload !== previousProps.pointers.preventHttpDownload ||
            pointers.releasePolicy !== previousProps.pointers.releasePolicy ||
            pointers.releaseDate !== previousProps.pointers.releaseDate ||
            pointers.publicLink !== previousProps.pointers.publicLink ||
            pointers.watermark !== previousProps.pointers.watermark ||
            pointers.expirationTime !== previousProps.pointers.expirationTime ||
            pointers.expirationPolicy !== previousProps.pointers.expirationPolicy ||
            pointers.expirationTime !== previousProps.pointers.expirationTime) {
            this.validCheck();
        }
    }
    componentWillUnmount() {
        this.subManager.unsubscribeAll();
    }
    render() {
        const { className, requirePasswordProtection, pointers, customView, inlineNotification } = this.props;
        return (React.createElement("div", { id: "AsperaSendPackage", className: `aspera-send-package ${className ? className : ''}`, "data-auto": "send-package" },
            !!inlineNotification && inlineNotification,
            this.recipientsArea,
            this.packageTitleField,
            this.fileUploadArea,
            this.packageMessageField,
            !!customView && customView,
            (!!pointers.password && requirePasswordProtection) && this.passwordProtection,
            this.extraContentArea));
    }
}
